import type { CatalogAudience, CatalogReleaseChannel } from './catalog';

export interface UserAccount {
    readonly uuid: string;
    readonly username: string;
    readonly email: string | null;
    readonly profileImage: string;
    readonly country: string;
    readonly language: string | null;
    readonly flags: UserAccountFlags;
    readonly audience: CatalogAudience;
    readonly subscription: Subscription | null;
    readonly joinedAt: string;
    readonly boosts: number;
    readonly boostedGames: {
        readonly [gameId: string]: number;
    };
    readonly answeredPolls: readonly string[];
    readonly activeExperiments?: {
        readonly [key: string]: number | null;
    };
    readonly releaseChannels?: readonly CatalogReleaseChannel[]; // optional for backwards compatibility
    readonly gdpr?: {
        readonly consentGiven: boolean;
        readonly consentRequired: boolean;
    };
    readonly features?: UserAccountFeatures;
    readonly remoteChannel?: string | null;
    readonly activeStreak: number | null; //initially null then # in days
    readonly rewardOffers?: RewardOffer[];
    readonly advertisedBenefits?: readonly string[];
    readonly featurebaseJwt?: string | null;
    readonly adViewData?: string;
}

export interface UserAccountFeature {
    readonly enabled: boolean;
}

export interface UserAccountFeatures {
    readonly timeLimit?: UserAccountFeature;
    readonly modControls?: UserAccountFeature;
}

export enum UserAccountFlags {
    UsernameSet = 1 << 0,
    EmailSet = 1 << 1,
    EmailVerified = 1 << 2,
    PasswordSet = 1 << 3,
    ProfileImageSet = 1 << 4,
    LeftAppFeedback = 1 << 5,
    Creator = 1 << 6,
    CommunityMember = 1 << 7,
    DiscordMember = 1 << 8,
    ProMember = 1 << 9,
    UsedRemote = 1 << 10,
    WasEverPro = 1 << 11,
    BoostedGame = 1 << 12,
    AdDeveloper = 1 << 13,
    TrainerTester = 1 << 14,
    GameMapEditor = 1 << 15,
    LabsAdmin = 1 << 19,
    LabsUser = 1 << 20,
}

export type SubscriptionType = 'active' | 'canceled' | 'expiring';
export type SubscriptionPeriod = 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';

export interface Subscription {
    readonly state: SubscriptionType;
    readonly period: SubscriptionPeriod;
    readonly gift: SubscriptionGift | null;
    readonly remoteChannel: string;
    readonly nextInvoice: Invoice | null;
    readonly pastDueInvoice: Invoice | null;
    readonly startedAt: string;
    readonly trialEndsAt: string | null;
    readonly endsAt: string | null;
}

export interface RewardOffer {
    readonly endsAt: string;
    readonly partner: string;
    readonly redemptionCode?: string;
    readonly rewardKey: string;
    readonly startsAt: string;
    readonly claimBy?: string;
    readonly active: boolean;
}

export interface SubscriptionGift {
    readonly senderName: string;
}

export interface Invoice {
    readonly date: string;
    readonly currency: string;
    readonly amount: string;
}

export interface EmailVerificationResponse {
    readonly email: string;
}

export interface RemoteAuthCodeResponse {
    readonly code: string;
    readonly expiresIn: number;
}
